import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    FormControlLabel,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SupportDashboardT, SupportUserT } from 'routes/Support/models';
import Flex from 'ui/Flex/Flex';
import BindSelector from './BindSelector';
import DashboardSelector from './DashboardSelector';
import UserSelector from './UserSelector';
import { useTransferDashboard } from './hooks/dashboards';
import { SupportModalTitle } from '../SupportModalTitle';

type Props = {
    isOpen: boolean;
    binds: ClientT[];
    onClose: () => void;
};

const TransferDashboardUserModal = ({ isOpen, onClose, binds }: Props) => {
    const [isTemplate, setIsTemplate] = useState(false);
    const [isTutorial, setIsTutorial] = useState(false);
    const { t } = useTranslation();

    const [selectedSourceBind, setSourceSelectedBind] = useState<
        string | undefined
    >(undefined);
    const [selectedSourceDashboard, setSourceSelectedDashboard] = useState<
        SupportDashboardT | null | undefined
    >(null);
    const [selectedTargetBind, setTargetSelectedBind] = useState<
        string | undefined
    >(undefined);
    const [selectedUser, setSelectedUser] = useState<
        SupportUserT | null | undefined
    >(null);

    const [transferDisabled, setTransferDisabled] = useState(false);
    const transferDashboard = useTransferDashboard();

    useEffect(() => {
        const disabled =
            !selectedSourceBind ||
            !selectedSourceDashboard ||
            !selectedTargetBind ||
            !selectedUser;
        setTransferDisabled(disabled);
    }, [
        selectedSourceBind,
        selectedSourceDashboard,
        selectedTargetBind,
        selectedUser,
    ]);

    const submit = () => {
        if (
            !selectedSourceBind ||
            !selectedSourceDashboard ||
            !selectedTargetBind ||
            !selectedUser
        )
            return;

        transferDashboard.mutate({
            sourceBind: selectedSourceBind,
            sourceDashboardKey: selectedSourceDashboard.key,
            targetBind: selectedTargetBind,
            targetUserId: selectedUser.id,
            isTemplate,
            isTutorial,
        });
    };

    return (
        <Dialog fullWidth maxWidth="lg" open={isOpen} onClose={onClose}>
            <SupportModalTitle
                title={t('support.importDashboard.title')}
                onClose={onClose}
            />
            <DialogContent sx={{ p: '2em' }}>
                <Flex
                    sx={{
                        justifyContent: 'space-evenly',
                        alignItems: 'stretch',
                        width: '100%',
                        flexFlow: 'row nowrap',
                    }}
                >
                    <BindSelector
                        binds={binds}
                        label={t('support.importDashboard.selectSourceClient')}
                        bindSelected={(bind) =>
                            setSourceSelectedBind(bind?.bind)
                        }
                    />
                    <DashboardSelector
                        bind={selectedSourceBind}
                        label={t(
                            'support.importDashboard.selectSourceDashboard',
                        )}
                        dashboardSelected={(dashboard) =>
                            setSourceSelectedDashboard(dashboard)
                        }
                    />
                    <BindSelector
                        binds={binds}
                        label={t('support.importDashboard.selectTargetClient')}
                        bindSelected={(bind) =>
                            setTargetSelectedBind(bind?.bind)
                        }
                    />
                    <UserSelector
                        bind={selectedTargetBind}
                        label={t('support.importDashboard.selectTargetUser')}
                        userSelected={(user) => setSelectedUser(user)}
                    />
                </Flex>
                <Flex sx={{ flexDirection: 'row-reverse', pt: '1em' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isTemplate}
                                onChange={(event) =>
                                    setIsTemplate(event.target.checked)
                                }
                            />
                        }
                        label={t('support.importDashboard.isTemplate')}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isTutorial}
                                onChange={(event) =>
                                    setIsTutorial(event.target.checked)
                                }
                            />
                        }
                        label={t('support.importDashboard.isTutorial')}
                    />
                </Flex>
            </DialogContent>
            <DialogActions sx={{ p: '2em' }}>
                <Button
                    sx={{ color: 'primary' }}
                    variant="contained"
                    onClick={() => submit()}
                    disabled={transferDisabled}
                >
                    {t('support.importDashboard.import')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TransferDashboardUserModal;
